import { useMemo } from 'react';

import Icon from 'components/icon/Icon';
import { cn } from 'lib/utils';

import {
  SymptomCheckerDetail,
  SymptomCheckerDetailResponse,
} from 'services/symptom-checker/type';
import { useTranslation } from 'react-i18next';

export default function ReportDetailInfo({
  detail,
}: {
  detail: SymptomCheckerDetailResponse;
}) {
  const { t } = useTranslation();

  const reportData: SymptomCheckerDetail = useMemo(() => {
    let parsed: SymptomCheckerDetail = {} as SymptomCheckerDetail;
    try {
      parsed = JSON.parse(detail?.ai_response || '{}');
    } catch (error) {
      console.log(error);
    }
    return parsed;
  }, [detail?.ai_response]);

  return (
    <div className='space-y-5'>
      <div className='bg-[#F1F5F9] rounded-3xl p-4 font-medium text-[#656D76] space-y-3'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center justify-center bg-white size-9 rounded-xl'>
            <Icon icon='report1' size={24} color='#1A68D8' />
          </div>
          <h1 className='font-semibold text-[#1A68D8] text-xl'>
            {t('custom_report')}
          </h1>
        </div>

        <p>{reportData?.initial_text}</p>

        {reportData?.location_of_lesion ? (
          <div className='border rounded-2xl p-4 bg-white border-[#1A68D8] space-y-2'>
            <h2 className='font-semibold text-[#1A68D8]'>
              {t('images_visual_analysis')}
            </h2>
            <ul className='pl-4 space-y-1 list-disc'>
              <li>{reportData?.location_of_lesion}</li>
              <li>{reportData?.lesion_specifications}</li>
            </ul>
          </div>
        ) : null}
      </div>

      <div className='bg-[#F1F5F9] rounded-3xl p-4 space-y-3'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center justify-center bg-white size-10 rounded-xl'>
            <Icon icon='findings' size={22} color='#1A68D8' />
          </div>
          <h1 className='font-semibold text-[#1A68D8] text-xl'>
            {t('main_findings')}
          </h1>
        </div>
        <div className='space-y-3 font-medium text-[#656D76]'>
          <p>{t('main_findings_info')}</p>

          {reportData?.first_hypothesis ? (
            <p className='text-[#656D76] font-medium'>
              <span className='text-[#1A68D8] font-semibold'>
                {reportData?.first_hypothesis?.first_hypothesis_name},
                {t('probability')}:{' '}
                {reportData?.first_hypothesis?.first_hypothesis_percentage}
              </span>{' '}
              [{reportData?.first_hypothesis?.first_hypothesis_text}]
            </p>
          ) : null}
          {reportData?.second_hypothesis ? (
            <p className='text-[#656D76] font-medium'>
              <span className='text-[#1A68D8] font-semibold'>
                {reportData?.second_hypothesis?.second_hypothesis_name},
                {t('probability')}:{' '}
                {reportData?.second_hypothesis?.second_hypothesis_percentage}
              </span>{' '}
              [{reportData?.second_hypothesis?.second_hypothesis_text}]
            </p>
          ) : null}
          {reportData?.third_hypothesis ? (
            <p className='text-[#656D76] font-medium'>
              <span className='text-[#1A68D8] font-semibold'>
                {reportData?.third_hypothesis?.third_hypothesis_name},
                {t('probability')}:{' '}
                {reportData?.third_hypothesis?.third_hypothesis_percentage}
              </span>{' '}
              [{reportData?.third_hypothesis?.third_hypothesis_text}]
            </p>
          ) : null}
          {reportData?.fourth_hypothesis ? (
            <p className='text-[#656D76] font-medium'>
              <span className='text-[#1A68D8] font-semibold'>
                {reportData?.fourth_hypothesis?.fourth_hypothesis_name},
                {t('probability')}:{' '}
                {reportData?.fourth_hypothesis?.fourth_hypothesis_percentage}
              </span>{' '}
              [{reportData?.fourth_hypothesis?.fourth_hypothesis_text}]
            </p>
          ) : null}

          <p>{t('hypothesis_disclaimer')}</p>
        </div>
      </div>

      <div className='bg-[#F1F5F9] rounded-3xl p-4 space-y-4'>
        <div className='flex items-center gap-2'>
          <div className='flex items-center justify-center bg-white size-9 rounded-xl'>
            <Icon icon='doctor' size={24} color='#1A68D8' />
          </div>
          <h1 className='font-semibold text-[#1A68D8] text-xl'>
            {t('recommendations')}
          </h1>
        </div>

        <div className='border rounded-2xl p-4 bg-white border-[#1A68D8] space-y-3'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center bg-[#F1F5F9] size-9 rounded-xl'>
              <Icon icon='avg-time' size={20} color='#1A68D8' />
            </div>
            <h2 className='font-semibold text-[#1A68D8] text-lg'>
              {t('immediately')}
            </h2>
          </div>

          <div className='space-y-3 font-medium text-[#656D76]'>
            {reportData?.immediately_recommendations?.restrictions ? (
              <p>
                <span className='text-[#1A68D8] font-semibold'>
                  {t('restrictions')}:
                </span>{' '}
                {reportData?.immediately_recommendations?.restrictions}
              </p>
            ) : null}
            {reportData?.immediately_recommendations?.hygiene ? (
              <p>
                <span className='text-[#1A68D8] font-semibold'>
                  {t('hygiene')}:
                </span>{' '}
                {reportData?.immediately_recommendations?.hygiene}
              </p>
            ) : null}
            {reportData?.immediately_recommendations?.home_remedies ? (
              <p>
                <span className='text-[#1A68D8] font-semibold'>
                  {t('home_remedies')}:
                </span>{' '}
                {reportData?.immediately_recommendations?.home_remedies}
              </p>
            ) : null}
            {reportData?.immediately_recommendations?.justification ? (
              <p>
                <span className='text-[#1A68D8] font-semibold'>
                  {t('reason')}:
                </span>{' '}
                {reportData?.immediately_recommendations?.justification}
              </p>
            ) : null}
          </div>
        </div>

        <div className='border rounded-2xl p-4 bg-white border-[#1A68D8] space-y-3'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center bg-[#F1F5F9] size-9 rounded-xl'>
              <Icon icon='heart-medical' size={20} color='#1A68D8' />
            </div>
            <h2 className='font-semibold text-[#1A68D8] text-lg'>
              {t('medical_consultations')}
            </h2>
          </div>

          <div className='space-y-4 font-medium text-[#656D76]'>
            {reportData?.medical_consultations?.length
              ? reportData?.medical_consultations?.map((consultation) => (
                  <div
                    key={consultation.specialty_name}
                    className={cn(
                      'border rounded-xl p-3 border-[#FF686499] space-y-1'
                    )}
                  >
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('specialty')}:{' '}
                      </span>{' '}
                      {consultation.specialty_name}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('reason')}:{' '}
                      </span>{' '}
                      {consultation.reason}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('urgency_level')}:{' '}
                      </span>{' '}
                      {consultation.urgency_level}
                    </p>
                  </div>
                ))
              : null}
          </div>
        </div>

        <div className='border rounded-2xl p-4 bg-white border-[#1A68D8] space-y-3'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center bg-[#F1F5F9] size-9 rounded-xl'>
              <Icon icon='document-add' size={20} color='#1A68D8' />
            </div>
            <h2 className='font-semibold text-[#1A68D8] text-lg'>
              {t('additional_exams')}
            </h2>
          </div>

          <div className='space-y-4 font-medium text-[#656D76]'>
            {reportData?.additional_exams?.length
              ? reportData?.additional_exams?.map((exam) => (
                  <div
                    key={exam.exam_name}
                    className={cn(
                      'border rounded-xl p-2 border-[#FF686499] space-y-1'
                    )}
                  >
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('exam')}:
                      </span>{' '}
                      {exam.exam_name}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('reason')}:{' '}
                      </span>{' '}
                      {exam.reason}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('preparation')}:
                      </span>{' '}
                      {exam.preparation}
                    </p>
                  </div>
                ))
              : null}
          </div>
        </div>

        <div className='border rounded-2xl p-4 bg-white border-[#1A68D8] space-y-2'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center bg-[#F1F5F9] size-9 rounded-xl'>
              <Icon icon='document-add' size={20} color='#1A68D8' />
            </div>
            <h2 className='font-semibold text-[#1A68D8] text-lg'>
              {t('symptoms_monitoring')}
            </h2>
          </div>

          <div className='space-y-2 font-medium text-[#656D76]'>
            {reportData?.symptom_monitoring?.length
              ? reportData?.symptom_monitoring?.map((monitor) => (
                  <div
                    key={monitor.frequency}
                    // className={cn('border rounded-xl p-2 border-[#FF6864]')}
                    className='space-y-1'
                  >
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('frequency')}:
                      </span>{' '}
                      {monitor.frequency}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('method')}:
                      </span>{' '}
                      {monitor.method}
                    </p>
                    <p>
                      <span className='text-[#FF6864] font-semibold'>
                        {t('reason')}:{' '}
                      </span>{' '}
                      {monitor.reason}
                    </p>
                  </div>
                ))
              : null}
          </div>
        </div>
      </div>

      {reportData?.medical_images?.length ? (
        <div className='bg-[#F1F5F9] rounded-3xl p-4 space-y-4'>
          <div className='flex items-center gap-2'>
            <div className='flex items-center justify-center bg-white size-9 rounded-xl'>
              <Icon icon='light-bulb' size={24} color='#1A68D8' />
            </div>
            <h1 className='font-semibold text-[#1A68D8] text-xl'>
              {t('reference_images')}
            </h1>
          </div>

          <div className='flex items-center gap-2'>
            {reportData.medical_images.map((image) => (
              <div className='h-20 aspect-square border-2 border-[#1A68D8] overflow-hidden rounded-xl'>
                <img
                  src={image}
                  alt='image'
                  className='object-fill overflow-hidden rounded-xl'
                />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
